import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { persistor } from "../store";
import {
  adminLogin,
  adminLogout,
  adminOtpVerify,
} from "../../../services/api/auth/authApi";
import { trustedLogout } from "../../../services/api/trusted/trustedApi";
import {
  PORTAL_ADMIN,
  PORTAL_TRUSTED,
  clearAuthPortal,
  getAuthPortal,
  setAuthPortal,
} from "../../../services/authPortal";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await adminLogin(credentials);
      const { access_token: token, otp_enabled, uuid } = response.data.data;

      if (otp_enabled) {
        return {
          otp_enabled: true,
          email: credentials.email,
          uuid,
        };
      }

      localStorage.setItem("authToken", token);
      setAuthPortal(PORTAL_ADMIN);
      return { token };
    } catch (error) {
      const errorMessage =
        error.response?.data?.data?.error ||
        "Login failed. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("authToken");
    const portal = getAuthPortal();

    try {
      if (token) {
        try {
          // A trusted logout also ends the officer's other trusted sessions.
          await (portal === PORTAL_TRUSTED
            ? trustedLogout(token)
            : adminLogout(token));
        } catch (apiError) {
          // Session may already be invalid; still clear local state.
        }
      }

      localStorage.removeItem("authToken");
      localStorage.removeItem("loginTime");
      clearAuthPortal();
      await persistor.purge();
      dispatch(clearAuth());
    } catch (error) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("loginTime");
      clearAuthPortal();
      dispatch(clearAuth());
    }
    return { portal };
  },
);

export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await adminOtpVerify({ email, otp });

      if (response.data.success === false) {
        return rejectWithValue(response.data.message);
      }

      const token = response.data.data.access_token;
      if (!token) {
        throw new Error("No token received");
      }

      localStorage.setItem("authToken", token);
      setAuthPortal(PORTAL_ADMIN);
      return {
        token,
        otp_enabled: response.data.data.otp_enabled,
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message?.en ||
        error.response?.data?.message ||
        error.response?.data?.data?.error ||
        "OTP verification failed. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    token: localStorage.getItem("authToken") || null,
    isAuthenticated: !!localStorage.getItem("authToken"),
    portal: getAuthPortal(),
    loading: false,
    error: null,
  },
  reducers: {
    clearAuth: (state) => {
      state.token = null;
      state.isAuthenticated = false;
      state.portal = PORTAL_ADMIN;
    },
    trustedSignedIn: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = true;
      state.portal = PORTAL_TRUSTED;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        if (action.payload.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.portal = PORTAL_ADMIN;
        }
        state.loading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.token = null;
        state.isAuthenticated = false;
        state.portal = PORTAL_ADMIN;
      })
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.portal = PORTAL_ADMIN;
        state.loading = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAuth } = authSlice.actions;

// Trusted-roles login (Nafath + TOTP) finished: store the trusted token.
export const completeTrustedLogin = (token) => (dispatch) => {
  localStorage.setItem("authToken", token);
  localStorage.setItem("loginTime", String(Date.now()));
  setAuthPortal(PORTAL_TRUSTED);
  dispatch(authSlice.actions.trustedSignedIn(token));
};
export default authSlice.reducer;
